import { useMemo, type ReactElement } from "react";
import type { Theme } from "@mui/material";
import { AllergiesAnalysis } from "src-next/modules/clinical-insights/components/analyses/AllergiesAnalysis";
import { ConductAnalysis } from "src-next/modules/clinical-insights/components/analyses/ConductAnalysis";
import { DiagnosticAnalysis } from "src-next/modules/clinical-insights/components/analyses/DiagnosticAnalysis";
import { PrescriptionAnalysis } from "src-next/modules/clinical-insights/components/analyses/PrescriptionAnalysis";
import { ProblemListAnalysis } from "src-next/modules/clinical-insights/components/analyses/ProblemListAnalysis";
import { ProtocolAnalysis } from "src-next/modules/clinical-insights/components/analyses/ProtocolAnalysis";
import { ServiceRequestAnalysis } from "src-next/modules/clinical-insights/components/analyses/ServiceRequestAnalysis";
import { TreatmentPlansAnalysis } from "src-next/modules/clinical-insights/components/analyses/TreatmentPlansAnalysis";
import type { AnalysisCardId } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import { getAnalysisAccentColor } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";

type SharedProps = Pick<
  ClinicalInsightChildProps,
  "dateRange" | "practitionerId" | "practitioner"
>;

type WorkspaceProps = Pick<
  ClinicalInsightChildProps,
  | "onPatientStatsChange"
  | "practitionerFilter"
  | "onSelectAllPractitioners"
  | "onPreviousPeriod"
  | "periodDisplayLabel"
>;

interface BuildAnalysisComponentsArgs {
  theme: Theme;
  selectedAnalysisId: AnalysisCardId;
  visitedAnalysisIds: Set<AnalysisCardId>;
  loadPhaseCallbacks: Record<AnalysisCardId, (phase: LoadPhase) => void>;
  sharedInsightProps: SharedProps;
  workspaceInsightProps: WorkspaceProps;
}

export function useAnalysisComponents({
  theme,
  selectedAnalysisId,
  visitedAnalysisIds,
  loadPhaseCallbacks,
  sharedInsightProps,
  workspaceInsightProps,
}: BuildAnalysisComponentsArgs): Record<AnalysisCardId, ReactElement> {
  return useMemo((): Record<AnalysisCardId, ReactElement> => {
    const accentFor = (id: AnalysisCardId) => getAnalysisAccentColor(theme, id);
    const ws = workspaceInsightProps;
    const active = (id: AnalysisCardId) => selectedAnalysisId === id;
    const warm = (id: AnalysisCardId) => visitedAnalysisIds.has(id);

    return {
      tratamentos: (
        <PrescriptionAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("tratamentos")}
          fetchWarm={warm("tratamentos")}
          onLoadPhaseChange={loadPhaseCallbacks.tratamentos}
          accentColor={accentFor("tratamentos")}
        />
      ),
      protocolos: (
        <ProtocolAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("protocolos")}
          fetchWarm={warm("protocolos")}
          onLoadPhaseChange={loadPhaseCallbacks.protocolos}
          accentColor={accentFor("protocolos")}
        />
      ),
      condutas: (
        <ConductAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("condutas")}
          fetchWarm={warm("condutas")}
          onLoadPhaseChange={loadPhaseCallbacks.condutas}
          accentColor={accentFor("condutas")}
        />
      ),
      problemas: (
        <ProblemListAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("problemas")}
          fetchWarm={warm("problemas")}
          onLoadPhaseChange={loadPhaseCallbacks.problemas}
          accentColor={accentFor("problemas")}
        />
      ),
      planos: (
        <TreatmentPlansAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("planos")}
          fetchWarm={warm("planos")}
          onLoadPhaseChange={loadPhaseCallbacks.planos}
          accentColor={accentFor("planos")}
        />
      ),
      diagnosticos: (
        <DiagnosticAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("diagnosticos")}
          fetchWarm={warm("diagnosticos")}
          onLoadPhaseChange={loadPhaseCallbacks.diagnosticos}
          accentColor={accentFor("diagnosticos")}
        />
      ),
      service: (
        <ServiceRequestAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("service")}
          fetchWarm={warm("service")}
          onLoadPhaseChange={loadPhaseCallbacks.service}
          accentColor={accentFor("service")}
        />
      ),
      alergias: (
        <AllergiesAnalysis
          {...sharedInsightProps}
          {...ws}
          isActive={active("alergias")}
          fetchWarm={warm("alergias")}
          onLoadPhaseChange={loadPhaseCallbacks.alergias}
          accentColor={accentFor("alergias")}
        />
      ),
    };
  }, [theme, selectedAnalysisId, visitedAnalysisIds, loadPhaseCallbacks, sharedInsightProps, workspaceInsightProps]);
}
